import { useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import { ConfirmDialog, ErrorState, LoadingState, Panel, StatusBadge } from '../components/ui.js';
import type { Tone } from '../components/ui.js';
import '../screens-2a.css';

/**
 * Jazyky e-mailů k závodu (D-330): které jazyky se pro závod používají (první je výchozí), stav překladů
 * šablon (k překladu, návrh, schváleno) a tlačítka na vytvoření návrhů a schválení. Neschválený jazyk se
 * nikdy neposílá; příjemce bez schválené verze dostane výchozí jazyk závodu.
 */
const LANGS: Array<[string, string]> = [
  ['cs', 'čeština'],
  ['en', 'angličtina'],
  ['de', 'němčina'],
  ['it', 'italština'],
  ['pl', 'polština'],
  ['sk', 'slovenština'],
  ['hu', 'maďarština'],
];
const STATUS: Record<string, { label: string; tone: Tone }> = {
  source: { label: 'zdroj', tone: 'neu' },
  missing: { label: 'chybí', tone: 'bad' },
  to_translate: { label: 'k překladu', tone: 'warn' },
  draft: { label: 'přeloženo (návrh)', tone: 'acc' },
  approved: { label: 'schváleno', tone: 'ok' },
};

interface Overview {
  languages: string[];
  defaultLanguage: string;
  templates: Array<{
    base: string;
    cells: Array<{ language: string; status: string; key: string }>;
  }>;
}

export function RaceLanguages({
  raceId,
  canWrite,
  canApprove,
}: {
  raceId: string;
  canWrite: boolean;
  canApprove: boolean;
}): JSX.Element {
  const client = useQueryClient();
  const key = ['race-languages', raceId];
  const q = useQuery<Overview>({
    queryKey: key,
    queryFn: () =>
      api.get(`/api/races/${encodeURIComponent(raceId)}/languages${qs({ brand: 'TGP' })}`),
  });
  const [draft, setDraft] = useState<string[] | null>(null);
  const [ask, setAsk] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const refresh = (): void => void client.invalidateQueries({ queryKey: key });
  const save = useMutation({
    mutationFn: (languages: string[]) =>
      api.put(`/api/races/${encodeURIComponent(raceId)}/languages`, { languages }),
    onSuccess: () => {
      setDraft(null);
      setNote('Jazyky uloženy.');
      refresh();
    },
  });
  const generate = useMutation({
    mutationFn: () =>
      api.post<{ created: unknown[]; skipped: unknown[] }>(
        `/api/races/${encodeURIComponent(raceId)}/translations`,
        { brand: 'TGP' },
      ),
    onSuccess: (r) => {
      setNote(
        `Vytvořeno ${r.created.length} návrhů překladu, přeskočeno ${r.skipped.length}. Návrh je třeba zkontrolovat a schválit; nic se neposílá ani nenahrává do SmartEmailingu.`,
      );
      refresh();
    },
  });
  const approve = useMutation({
    mutationFn: (templateKey: string) =>
      api.post(`/api/races/${encodeURIComponent(raceId)}/translations/approve`, {
        key: templateKey,
      }),
    onSuccess: () => {
      setAsk(null);
      setNote('Překlad schválen a publikován (do SmartEmailingu se nenahrává, to děláte zvlášť).');
      refresh();
    },
  });

  if (q.isLoading) return <LoadingState what="jazyky závodu" />;
  if (q.isError || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const current = draft ?? q.data.languages;
  const toggle = (code: string): void => {
    const next = current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
    setDraft(next.length === 0 ? current : next);
  };

  return (
    <Panel
      title="Jazyky e-mailů"
      subtitle={`Výchozí jazyk závodu: ${q.data.defaultLanguage}. Příjemce bez schválené verze ve svém jazyce dostane výchozí jazyk a rozhodnutí se zapíše.`}
    >
      <div className="s2a-filters" data-testid="race-languages">
        {LANGS.map(([code, name]) => (
          <label key={code} className="field">
            <input
              type="checkbox"
              checked={current.includes(code)}
              disabled={!canWrite}
              onChange={() => toggle(code)}
            />{' '}
            {name}
            {code === current[0] ? ' (výchozí)' : ''}
          </label>
        ))}
        {canWrite ? (
          <>
            <button
              type="button"
              className="btn primary"
              disabled={draft === null || save.isPending}
              onClick={() => save.mutate(current)}
            >
              Uložit jazyky
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => generate.mutate()}
              disabled={generate.isPending}
            >
              Vytvořit návrhy překladů
            </button>
          </>
        ) : null}
      </div>
      <p className="muted">
        Výchozí je první uložený jazyk; chcete-li změnit výchozí, odškrtněte jazyky a zaškrtněte je
        znovu v požadovaném pořadí.
      </p>
      {note ? (
        <p className="note" role="status">
          {note}
        </p>
      ) : null}
      {q.data.templates.length === 0 ? (
        <p className="muted">
          K tomuto závodu zatím nejsou žádné české šablony (hledá se podle názvu okruhu v klíči
          šablony).
        </p>
      ) : (
        <div className="scroll">
          <table data-testid="race-language-table">
            <thead>
              <tr>
                <th>Šablona</th>
                {q.data.languages.map((l) => (
                  <th key={l}>{l}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {q.data.templates.map((t) => (
                <tr key={t.base}>
                  <td>
                    <code>{t.base}</code>
                  </td>
                  {t.cells.map((c) => (
                    <td key={c.language} title={c.key}>
                      <StatusBadge tone={STATUS[c.status]?.tone ?? 'neu'}>
                        {STATUS[c.status]?.label ?? c.status}
                      </StatusBadge>
                      {canApprove && c.status === 'draft' ? (
                        <button type="button" className="btn" onClick={() => setAsk(c.key)}>
                          Schválit
                        </button>
                      ) : null}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {generate.error ? <ErrorState error={generate.error} /> : null}
      {approve.error ? <ErrorState error={approve.error} /> : null}
      <ConfirmDialog
        open={ask !== null}
        title="Schválit překlad?"
        confirmLabel="Schválit a publikovat"
        busy={approve.isPending}
        onCancel={() => setAsk(null)}
        onConfirm={() => ask && approve.mutate(ask)}
      >
        Překlad <code>{ask}</code> se publikuje a od teď se smí posílat příjemcům s tímto jazykem.
        Zkontrolujte ho nejdřív v editoru šablon. Do SmartEmailingu se nic nenahrává.
      </ConfirmDialog>
    </Panel>
  );
}
